import { type FunctionComponent } from 'react'
import { ModalBody } from '@retailer-platform/shared-components'
import { CloseIcon } from '@instacart/ids-core'
import {
  Button,
  Flex,
  spacing,
  TextArea,
  Text,
  TextTitleRow,
  IconButton,
  TextField,
} from '@retailer-platform/shared-components/src/tds'
import { useDashMessages } from '../../../../intl/intl.hooks'
import { PartnerNamesTextArea } from './PartnerNamesTextArea'

export interface RequestAccessPageProps {
  reason: string
  ticketUrl: string
  onTicketUrlChange: (ticketUrl: string) => void
  onReasonChange: (reason: string) => void
  onCancel: () => void
  onRequestAccess: () => void
  onClose: () => void
  partnerNames?: string[]
  isLoading?: boolean
  error?: Error | null
}

export const RequestAccessPage: FunctionComponent<RequestAccessPageProps> = ({
  reason,
  ticketUrl,
  onTicketUrlChange,
  onReasonChange,
  onCancel,
  onRequestAccess,
  onClose,
  partnerNames,
  isLoading = false,
  error,
}) => {
  const messages = useDashMessages({
    title: 'partnerWriteAccess.breakglassModal.title',
    subtitle: 'partnerWriteAccess.breakglassModal.subtitle',
    instructions: 'partnerWriteAccess.breakglassModal.instructions',
    reasonLabel: 'partnerWriteAccess.breakglassModal.reasonLabel',
    reasonPlaceholder: 'partnerWriteAccess.breakglassModal.reasonPlaceholder',
    reasonHint: 'partnerWriteAccess.breakglassModal.reasonHint',
    ticketUrlLabel: 'partnerWriteAccess.breakglassModal.ticketUrlLabel',
    ticketUrlHint: 'partnerWriteAccess.breakglassModal.ticketUrlHint',
    ticketUrlPlaceholder: 'partnerWriteAccess.breakglassModal.ticketUrlPlaceholder',
    cancelButton: 'partnerWriteAccess.breakglassModal.cancelButton',
    requestButton: 'partnerWriteAccess.breakglassModal.requestButton',
    closeModalLabel: 'partnerWriteAccess.breakglassModal.closeModalLabel',
    errorMessage: 'partnerWriteAccess.breakglassModal.errorMessage',
  })

  return (
    <>
      <Flex.Column padding={spacing.s24} width="100%">
        <TextTitleRow
          title={messages.title}
          titleTextProps={{ as: 'h1' }}
          rightSection={
            <IconButton
              icon={CloseIcon}
              onClick={onClose}
              variant="unstyled"
              aria-label={messages.closeModalLabel}
            />
          }
        />
      </Flex.Column>
      <ModalBody>
        <Flex.Column gap={spacing.s24}>
          <Flex.Column gap={spacing.s8}>
            <Text typography="subtitle">{messages.subtitle}</Text>
            <PartnerNamesTextArea partnerNames={partnerNames} />
            <Text typography="bodyRegular">{messages.instructions}</Text>
          </Flex.Column>

          <Flex.Column gap={spacing.s16}>
            <TextArea
              value={reason}
              labelProps={{
                label: messages.reasonLabel,
              }}
              description={messages.reasonHint}
              onChange={e => onReasonChange(e.target.value)}
              placeholder={messages.reasonPlaceholder}
              rows={3}
              aria-required="true"
            />
            <TextField
              value={ticketUrl}
              labelProps={{
                label: messages.ticketUrlLabel,
              }}
              description={messages.ticketUrlHint}
              onChange={e => onTicketUrlChange(e.target.value)}
              placeholder={messages.ticketUrlPlaceholder}
              aria-required="true"
            />
            {error && (
              <Text typography="bodyRegular" color="systemDetrimentalDark">
                {messages.errorMessage}
              </Text>
            )}
          </Flex.Column>

          <Flex gap={spacing.s16} justifyContent="flex-end">
            <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
              {messages.cancelButton}
            </Button>
            <Button
              variant="primary"
              onClick={onRequestAccess}
              disabled={!reason.trim() || !ticketUrl.trim()}
              loading={isLoading}
            >
              {messages.requestButton}
            </Button>
          </Flex>
        </Flex.Column>
      </ModalBody>
    </>
  )
}
